"use client"

import { useMemo, useState } from "react"
import { motion } from "framer-motion"
import { Bar, Doughnut, Line } from "react-chartjs-2"
import { ListTodo, GraduationCap, TrendingUp, TrendingDown, Trophy, CalendarCheck, UserPlus, Activity, Flame, ArrowUpRight, ArrowDownRight, Users } from "@/lib/icons"
import { formatNumber, formatPercentage } from "@/lib/utils"
import { LEVEL_PALETTE, COMPLETED_STATUSES } from "./constants"
import { normalizeStatus } from "@/lib/supabase"
import { LeadSourceBadge } from "@/components/kanban/lead-source-badge"

// ── Chart.js datalabels plugin (inline lightweight) ──
const datalabelsPlugin = {
    id: "customDatalabels",
    afterDatasetsDraw(chart) {
        const { ctx } = chart
        chart.data.datasets.forEach((dataset, di) => {
            const meta = chart.getDatasetMeta(di)
            if (!meta.hidden) {
                meta.data.forEach((el, i) => {
                    const val = dataset.data[i]
                    if (val > 0) {
                        ctx.save()
                        ctx.fillStyle = chart.options?.scales?.y ? "#64748b" : "#fff"
                        ctx.font = "bold 11px Inter, sans-serif"
                        ctx.textAlign = "center"
                        ctx.textBaseline = "bottom"
                        if (chart.options?.scales?.y) {
                            ctx.fillText(val, el.x, el.y - 4)
                        }
                        ctx.restore()
                    }
                })
            }
        })
    },
}

// Paleta tinto corporativa
const TINTO = {
    500: "#d94468",
    700: "#7a1b33",
    800: "#6b1829",
}

export function MetricsCharts({
    leadsByStatus = [],
    leadsByLevel = [],
    summary = {},
    isDarkMode = false,
    leads = [],
}) {
    const textColor = isDarkMode ? "rgba(248,250,252,0.85)" : "#475569"
    const gridColor = isDarkMode ? "rgba(148,163,184,0.12)" : "rgba(226,232,240,0.5)"

    // Pipeline data
    const pipelineStatuses = useMemo(() => {
        if (!leadsByStatus || !Array.isArray(leadsByStatus)) return []
        // Mostramos "Inscrito" en el pipeline (como ya se muestra "Archivado"):
        // es el resultado del embudo y el dato más importante del corte mensual.
        return leadsByStatus.filter((s) => s.key !== "spam")
    }, [leadsByStatus])

    const totalPipeline = useMemo(
        () => pipelineStatuses.reduce((sum, s) => sum + (Number(s.count) || 0), 0),
        [pipelineStatuses]
    )

    const statusBarData = useMemo(() => ({
        labels: pipelineStatuses.map((s) => s.name),
        datasets: [{
            label: "Leads",
            data: pipelineStatuses.map((s) => Number(s.count) || 0),
            backgroundColor: pipelineStatuses.map((s) => {
                const c = s.color ?? "#0ea5e9"
                return isDarkMode ? `${c}bb` : `${c}cc`
            }),
            hoverBackgroundColor: pipelineStatuses.map((s) => s.color ?? "#0ea5e9"),
            borderRadius: 8,
            maxBarThickness: 38,
            minBarLength: 3,
        }],
    }), [pipelineStatuses, isDarkMode])

    const isMobile = typeof window !== "undefined" && window.innerWidth < 640

    const barOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 900, easing: "easeOutQuart" },
            layout: { padding: { top: 20, bottom: 0, left: 0, right: 0 } },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: isDarkMode ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.97)",
                    titleColor: isDarkMode ? "#f1f5f9" : "#0f172a",
                    bodyColor: isDarkMode ? "#cbd5e1" : "#475569",
                    borderColor: isDarkMode ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.8)",
                    borderWidth: 1,
                    cornerRadius: 10,
                    padding: 10,
                    callbacks: {
                        label: (ctx) => {
                            const pct = totalPipeline > 0 ? ((ctx.parsed.y / totalPipeline) * 100).toFixed(1) : 0
                            return ` ${ctx.parsed.y} leads (${pct}%)`
                        },
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    grid: { color: gridColor, drawBorder: false },
                    ticks: { color: textColor, font: { size: isMobile ? 9 : 10, weight: "500" }, maxTicksLimit: 5, stepSize: 1, precision: 0 },
                    border: { display: false },
                },
                x: {
                    grid: { display: false },
                    ticks: { color: textColor, font: { size: isMobile ? 7 : 10, weight: "500" }, maxRotation: isMobile ? 55 : 25, minRotation: 0 },
                    border: { display: false },
                },
            },
        }),
        [gridColor, textColor, isMobile, isDarkMode, totalPipeline]
    )

    // Level data
    const levelsSafe = useMemo(() => {
        if (!leadsByLevel || !Array.isArray(leadsByLevel)) return []
        return leadsByLevel.filter((l) => l && l.name && l.count > 0)
    }, [leadsByLevel])

    const totalLevels = useMemo(() => levelsSafe.reduce((sum, l) => sum + l.count, 0), [levelsSafe])

    const levelData = useMemo(() => ({
        labels: levelsSafe.map((l) => l.name),
        datasets: [{
            data: levelsSafe.map((l) => l.count),
            backgroundColor: LEVEL_PALETTE.map((c) => `${c}dd`),
            hoverBackgroundColor: LEVEL_PALETTE,
            borderWidth: 2,
            borderColor: isDarkMode ? "#0f172a" : "#ffffff",
            spacing: 1,
        }],
    }), [levelsSafe, isDarkMode])

    const doughnutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: isDarkMode ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.97)",
                titleColor: isDarkMode ? "#f1f5f9" : "#0f172a",
                bodyColor: isDarkMode ? "#cbd5e1" : "#475569",
                borderColor: isDarkMode ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.8)",
                borderWidth: 1,
                cornerRadius: 10,
                padding: 10,
                callbacks: {
                    label: (ctx) => {
                        const pct = totalLevels > 0 ? ((ctx.parsed / totalLevels) * 100).toFixed(1) : 0
                        return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`
                    },
                },
            },
        },
        animation: { duration: 1200, easing: "easeOutQuart" },
        cutout: "58%",
    }), [isDarkMode, totalLevels])

    return (
        <div className="space-y-5">
            {/* ── Pipeline + Levels ── */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                {/* Pipeline — 3 cols */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: "tween", duration: 0.45 }}
                    className="lg:col-span-3 group relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm transition-all hover:shadow-md dark:border-slate-700/60 dark:bg-slate-900/80"
                >
                    <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-rose-500/10 blur-2xl" />
                    <div className="relative flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 shadow-sm dark:bg-rose-950/40" style={{ color: TINTO[700] }}>
                                <ListTodo className="h-4 w-4" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Pipeline de Leads</h3>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">Distribucion por etapa</p>
                            </div>
                        </div>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300 shadow-sm">
                            {totalPipeline} total
                        </span>
                    </div>
                    <div className="h-[260px] sm:h-[300px]">
                        {pipelineStatuses.length > 0 ? (
                            <Bar data={statusBarData} options={barOptions} plugins={[datalabelsPlugin]} />
                        ) : (
                            <div className="flex h-full items-center justify-center text-sm text-slate-400">Sin datos</div>
                        )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
                        {pipelineStatuses.filter(s => s.count > 0).map((s) => (
                            <span key={s.key} className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                                <span className="h-2 w-2 rounded-full flex-shrink-0 shadow-sm" style={{ background: s.color }} />
                                {s.name}: <b className="text-slate-700 dark:text-slate-200">{s.count}</b>
                            </span>
                        ))}
                    </div>
                </motion.div>

                {/* Levels — 2 cols */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: "tween", duration: 0.45, delay: 0.1 }}
                    className="lg:col-span-2 group relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm transition-all hover:shadow-md dark:border-slate-700/60 dark:bg-slate-900/80"
                >
                    <div className="pointer-events-none absolute -left-6 -top-6 h-24 w-24 rounded-full bg-rose-500/10 blur-2xl" />
                    <div className="relative flex items-center gap-2.5 mb-4">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 shadow-sm dark:bg-rose-950/40" style={{ color: TINTO[700] }}>
                            <GraduationCap className="h-4 w-4" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Niveles Educativos</h3>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">Distribucion de alumnos</p>
                        </div>
                    </div>
                    <div className="relative flex items-center justify-center h-[220px] sm:h-[250px]">
                        {levelsSafe.length > 0 ? (
                            <>
                                <Doughnut data={levelData} options={doughnutOptions} />
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <p className="text-3xl font-extrabold text-slate-900 dark:text-white">{totalLevels}</p>
                                    <p className="text-xs text-slate-400 font-medium">alumnos</p>
                                </div>
                            </>
                        ) : (
                            <p className="text-sm text-slate-400">Sin datos</p>
                        )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {levelsSafe.slice(0, 6).map((level, i) => {
                            const pct = totalLevels > 0 ? ((level.count / totalLevels) * 100).toFixed(0) : 0
                            return (
                                <span key={level.name} className="inline-flex items-center gap-1.5 rounded-full border border-slate-100 bg-slate-50/80 px-2.5 py-1 text-[11px] font-medium text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
                                    <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: LEVEL_PALETTE[i % LEVEL_PALETTE.length] }} />
                                    {level.name}: {level.count} ({pct}%)
                                </span>
                            )
                        })}
                    </div>
                </motion.div>
            </div>

            {/* ── Embudo + Velocidad + Tendencia (compacto) ── */}
            <InscritosAnalytics leads={leads} isDarkMode={isDarkMode} />

        </div>
    )
}

export default MetricsCharts

// ═══════════════════════════════════════════════════
// INSCRITOS HERO — Tarjeta prominente de inscritos
// ═══════════════════════════════════════════════════

function InscritosHero({ leads = [] }) {
    const inscritosLeads = useMemo(() =>
        leads.filter(l => COMPLETED_STATUSES.has(normalizeStatus(l.estado_lead)))
    , [leads])

    const totalInscritos = inscritosLeads.length
    const totalLeads = leads.length
    const conversionRate = totalLeads > 0 ? (totalInscritos / totalLeads) * 100 : 0

    // Inscritos esta semana
    const inscritosThisWeek = useMemo(() => {
        const now = new Date()
        const weekStart = new Date(now)
        weekStart.setDate(now.getDate() - now.getDay())
        weekStart.setHours(0, 0, 0, 0)
        return inscritosLeads.filter(l => {
            const date = l.fecha_inscripcion ? new Date(l.fecha_inscripcion) : new Date(l.updated_at || l.created_at)
            return date >= weekStart
        }).length
    }, [inscritosLeads])

    // Inscritos este mes
    const inscritosThisMonth = useMemo(() => {
        const now = new Date()
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
        return inscritosLeads.filter(l => {
            const date = l.fecha_inscripcion ? new Date(l.fecha_inscripcion) : new Date(l.updated_at || l.created_at)
            return date >= monthStart
        }).length
    }, [inscritosLeads])

    // Desglose por asesor
    const inscritosByAsesor = useMemo(() => {
        const map = {}
        inscritosLeads.forEach(l => {
            const name = l.asesor_nombre || l.asesor_email || "Sin asignar"
            if (!map[name]) map[name] = { nombre: name, count: 0, leads: [] }
            map[name].count++
            map[name].leads.push(l)
        })
        return Object.values(map).sort((a, b) => b.count - a.count)
    }, [inscritosLeads])

    if (!leads.length) return null

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="relative overflow-hidden rounded-xl border border-slate-200/60 bg-white shadow-sm dark:border-slate-700/60 dark:bg-slate-900/80"
        >
            {/* Accent bar */}
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500" />

            <div className="relative px-4 py-3 sm:px-5">
                {/* Fila principal */}
                <div className="flex items-center gap-4 sm:gap-6">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-950/40 shadow-sm flex-shrink-0">
                            <Trophy className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-black text-slate-900 dark:text-white tabular-nums leading-none">{totalInscritos}</p>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-0.5">Inscritos</p>
                        </div>
                    </div>

                    <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700" />

                    <div className="flex items-center gap-5 flex-1">
                        <div>
                            <p className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 tabular-nums">{formatPercentage(conversionRate)}</p>
                            <p className="text-[9px] text-slate-400">conversion</p>
                        </div>
                        <div>
                            <p className="text-sm font-extrabold text-slate-800 dark:text-white tabular-nums">{inscritosThisWeek}</p>
                            <p className="text-[9px] text-slate-400">esta semana</p>
                        </div>
                        <div>
                            <p className="text-sm font-extrabold text-slate-800 dark:text-white tabular-nums">{inscritosThisMonth}</p>
                            <p className="text-[9px] text-slate-400">este mes</p>
                        </div>
                    </div>

                    <span className="hidden sm:inline-flex text-[10px] font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-full px-2.5 py-1">
                        de {formatNumber(totalLeads)} leads
                    </span>
                </div>

                {/* Desglose por asesor — compacto con barra proporcional */}
                {inscritosByAsesor.length > 0 && totalInscritos > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Inscritos por asesor</p>
                        <div className="flex flex-wrap gap-2">
                            {inscritosByAsesor.map(({ nombre, count }) => {
                                const firstName = nombre.split(" ")[0]
                                const pct = totalInscritos > 0 ? Math.round((count / totalInscritos) * 100) : 0
                                return (
                                    <div key={nombre} className="flex items-center gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 px-2.5 py-1.5 ring-1 ring-slate-100 dark:ring-slate-700/50">
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 text-[10px] font-bold flex-shrink-0">
                                            {firstName.charAt(0).toUpperCase()}
                                        </div>
                                        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200 leading-none">{firstName}</p>
                                        <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 tabular-nums">{count}</span>
                                        <span className="text-[9px] text-slate-400">({pct}%)</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    )
}

// ═══════════════════════════════════════════════════
// INSCRITOS ANALYTICS — Embudo + Tendencias + Velocidad
// ═══════════════════════════════════════════════════

function InscritosAnalytics({ leads = [], isDarkMode = false }) {
    const [showAllInscritos, setShowAllInscritos] = useState(false)
    // ── Tendencia diaria (ultimos 30 dias) — incluye desglose por canal ──
    const dailyTrend = useMemo(() => {
        if (!leads?.length) return []
        const normalizeSrc = (s) => {
            const v = (s || "").toLowerCase()
            if (v === "facebook") return "meta_ads"
            if (v === "whatsapp") return "whatsapp_directo"
            return v
        }
        const now = new Date()
        const days = []
        for (let d = 29; d >= 0; d--) {
            const dayStart = new Date(now)
            dayStart.setDate(now.getDate() - d)
            dayStart.setHours(0, 0, 0, 0)
            const dayEnd = new Date(dayStart)
            dayEnd.setDate(dayStart.getDate() + 1)
            const dayLeads = leads.filter(l => {
                const created = new Date(l.created_at)
                return created >= dayStart && created < dayEnd
            })
            const dayInscritos = leads.filter(l => {
                if (!COMPLETED_STATUSES.has(normalizeStatus(l.estado_lead))) return false
                const date = l.inscripcion_confirmada_at ? new Date(l.inscripcion_confirmada_at) : (l.fecha_inscripcion ? new Date(l.fecha_inscripcion) : new Date(l.updated_at || l.created_at))
                return date >= dayStart && date < dayEnd
            }).length
            const label = dayStart.toLocaleDateString("es-MX", { day: "2-digit", month: "short" })

            // Conteo por canal del día
            const bySource = { meta_ads: 0, whatsapp_directo: 0, landing: 0, recomendacion: 0 }
            for (const l of dayLeads) {
                const src = normalizeSrc(l.source)
                if (src in bySource) bySource[src]++
            }

            days.push({
                label,
                leads: dayLeads.length,
                inscritos: dayInscritos,
                ...bySource,
            })
        }
        return days
    }, [leads])

    // ── Inscripciones recientes ──
    const recentInscritos = useMemo(() => {
        if (!leads?.length) return []
        return leads
            .filter(l => COMPLETED_STATUSES.has(normalizeStatus(l.estado_lead)))
            .sort((a, b) => {
                const dateA = a.fecha_inscripcion || a.updated_at || a.created_at
                const dateB = b.fecha_inscripcion || b.updated_at || b.created_at
                return new Date(dateB) - new Date(dateA)
            })
            .slice(0, 15)
    }, [leads])

    if (!leads?.length) return null

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
        >
            {/* Section Header */}
            <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 shadow-sm dark:bg-emerald-950/50 dark:text-emerald-400">
                    <TrendingUp className="h-4.5 w-4.5" />
                </div>
                <div>
                    <h2 className="text-base font-bold text-slate-900 dark:text-white">Analitica de Conversion</h2>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Tendencias e inscripciones recientes</p>
                </div>
            </div>

            {/* ── Tendencia Diaria — grafica de area ── */}
            {dailyTrend.length > 0 && (
                <DailyTrendChart dailyTrend={dailyTrend} isDarkMode={isDarkMode} />
            )}

            {/* ── Inscripciones recientes — tabla compacta colapsable ── */}
            {recentInscritos.length > 0 && (() => {
                const visibleInscritos = showAllInscritos ? recentInscritos : recentInscritos.slice(0, 3)
                const hiddenCount = recentInscritos.length - 3
                return (
                <div className="rounded-2xl border border-slate-200/60 bg-white shadow-sm dark:border-slate-700/60 dark:bg-slate-900/80 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                            <Trophy className="h-3.5 w-3.5" />
                        </div>
                        <h3 className="text-xs font-bold text-slate-900 dark:text-white">Inscripciones Recientes</h3>
                        <span className="text-[10px] text-slate-400 ml-auto">{recentInscritos.length} registros</span>
                    </div>
                    <div className={`overflow-x-auto ${showAllInscritos ? "max-h-[280px] overflow-y-auto" : ""}`}>
                        <table className="w-full text-left">
                            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 z-10">
                                <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                    <th className="px-4 py-2">Prospecto</th>
                                    <th className="px-3 py-2 hidden sm:table-cell">Nivel</th>
                                    <th className="px-3 py-2">Asesor</th>
                                    <th className="px-3 py-2 text-right">Fecha</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                                {visibleInscritos.map((lead) => {
                                    const displayDate = lead.fecha_inscripcion || lead.updated_at || lead.created_at
                                    const asesor = lead.asesor_nombre || lead.asesor_email || ""
                                    const asesorFirst = asesor ? asesor.split(" ")[0] : null
                                    return (
                                        <tr key={lead.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                            <td className="px-4 py-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400 text-[9px] font-bold flex-shrink-0">
                                                        {(lead.nombre_tutor || "?").charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="text-xs font-semibold text-slate-800 dark:text-white truncate max-w-[140px]">{lead.nombre_tutor || "Sin nombre"}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 hidden sm:table-cell">
                                                <span className="text-[10px] text-slate-500 dark:text-slate-400">{lead.nivel_educativo || "-"}</span>
                                            </td>
                                            <td className="px-3 py-2">
                                                {asesorFirst ? (
                                                    <span className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400">{asesorFirst}</span>
                                                ) : (
                                                    <span className="text-[10px] text-slate-300">-</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <span className="text-[10px] text-slate-400 tabular-nums">
                                                    {displayDate ? new Date(displayDate).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }) : ""}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    {hiddenCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setShowAllInscritos((v) => !v)}
                            className="w-full text-center py-2 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20 hover:bg-emerald-100 dark:hover:bg-emerald-950/40 transition-colors border-t border-emerald-100 dark:border-emerald-900/30"
                        >
                            {showAllInscritos
                                ? `Ver menos`
                                : `Ver ${hiddenCount} inscripcion${hiddenCount !== 1 ? "es" : ""} más`}
                        </button>
                    )}
                </div>
                )
            })()}
        </motion.div>
    )
}

// ═══════════════════════════════════════════════════
// DAILY TREND CHART — Grafica de area (leads + inscritos por dia)
// ═══════════════════════════════════════════════════

// Catálogo de canales disponibles para los toggles
const CHANNEL_FILTERS = [
    { key: "meta_ads",         label: "Meta Ads",       color: "#0866FF" },
    { key: "whatsapp_directo", label: "WhatsApp",       color: "#25D366" },
    { key: "landing",          label: "Landing Page",   color: "#7a1b33" },
    { key: "recomendacion",    label: "Recomendación",  color: "#f59e0b" },
]

function DailyTrendChart({ dailyTrend = [], isDarkMode = false }) {
    const textColor = isDarkMode ? "rgba(248,250,252,0.7)" : "#64748b"
    const gridColor = isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(226,232,240,0.6)"

    // Toggles de canales activos (ninguno por defecto)
    const [activeChannels, setActiveChannels] = useState(new Set())
    const toggleChannel = (key) => {
        setActiveChannels((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const totalLeads = useMemo(() => dailyTrend.reduce((s, d) => s + d.leads, 0), [dailyTrend])
    const totalInscritos = useMemo(() => dailyTrend.reduce((s, d) => s + d.inscritos, 0), [dailyTrend])
    const avgLeadsPerDay = useMemo(() => dailyTrend.length > 0 ? (totalLeads / dailyTrend.length).toFixed(1) : 0, [totalLeads, dailyTrend])
    const avgInscritosPerDay = useMemo(() => dailyTrend.length > 0 ? (totalInscritos / dailyTrend.length).toFixed(1) : 0, [totalInscritos, dailyTrend])
    const conversionRate = useMemo(() => totalLeads > 0 ? ((totalInscritos / totalLeads) * 100).toFixed(1) : 0, [totalLeads, totalInscritos])

    // Totales por canal (para mostrar en el botón de toggle)
    const channelTotals = useMemo(() => {
        const totals = { meta_ads: 0, whatsapp_directo: 0, landing: 0, recomendacion: 0 }
        for (const d of dailyTrend) {
            for (const k of Object.keys(totals)) {
                totals[k] += d[k] || 0
            }
        }
        return totals
    }, [dailyTrend])

    const peakDay = useMemo(() => {
        const max = Math.max(...dailyTrend.map(d => d.leads))
        return dailyTrend.find(d => d.leads === max)
    }, [dailyTrend])

    // Tendencia: compara ultimos 7 dias vs 7 anteriores
    const weekTrend = useMemo(() => {
        if (dailyTrend.length < 14) return { leads: 0, inscritos: 0 }
        const recent7 = dailyTrend.slice(-7)
        const prev7 = dailyTrend.slice(-14, -7)
        const recentLeads = recent7.reduce((s, d) => s + d.leads, 0)
        const prevLeads = prev7.reduce((s, d) => s + d.leads, 0)
        const recentInscritos = recent7.reduce((s, d) => s + d.inscritos, 0)
        const prevInscritos = prev7.reduce((s, d) => s + d.inscritos, 0)
        return {
            leads: prevLeads > 0 ? Math.round(((recentLeads - prevLeads) / prevLeads) * 100) : 0,
            inscritos: prevInscritos > 0 ? Math.round(((recentInscritos - prevInscritos) / prevInscritos) * 100) : 0,
        }
    }, [dailyTrend])

    const chartData = useMemo(() => {
        const baseDatasets = [
            {
                label: "Leads entrantes",
                data: dailyTrend.map(d => d.leads),
                borderColor: isDarkMode ? "#38bdf8" : "#0ea5e9",
                backgroundColor: isDarkMode
                    ? "rgba(56,189,248,0.08)"
                    : "rgba(14,165,233,0.08)",
                borderWidth: 2.5,
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: isDarkMode ? "#38bdf8" : "#0ea5e9",
                pointHoverBorderColor: "#fff",
                pointHoverBorderWidth: 2,
            },
            {
                label: "Inscritos",
                data: dailyTrend.map(d => d.inscritos),
                borderColor: "#10b981",
                backgroundColor: isDarkMode
                    ? "rgba(16,185,129,0.08)"
                    : "rgba(16,185,129,0.1)",
                borderWidth: 2.5,
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: "#10b981",
                pointHoverBorderColor: "#fff",
                pointHoverBorderWidth: 2,
            },
        ]

        // Agregar datasets de los canales activos
        const channelDatasets = CHANNEL_FILTERS
            .filter((c) => activeChannels.has(c.key))
            .map((c) => ({
                label: c.label,
                data: dailyTrend.map((d) => d[c.key] || 0),
                borderColor: c.color,
                backgroundColor: "transparent",
                borderWidth: 2,
                borderDash: [4, 4],
                fill: false,
                tension: 0.35,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHoverBackgroundColor: c.color,
                pointHoverBorderColor: "#fff",
                pointHoverBorderWidth: 2,
            }))

        return {
            labels: dailyTrend.map(d => d.label),
            datasets: [...baseDatasets, ...channelDatasets],
        }
    }, [dailyTrend, isDarkMode, activeChannels])

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        animation: { duration: 1000, easing: "easeOutQuart" },
        layout: { padding: { top: 4, right: 4, bottom: 0, left: 0 } },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: isDarkMode ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.97)",
                titleColor: isDarkMode ? "#f1f5f9" : "#0f172a",
                bodyColor: isDarkMode ? "#cbd5e1" : "#475569",
                borderColor: isDarkMode ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.8)",
                borderWidth: 1,
                cornerRadius: 12,
                padding: 14,
                titleFont: { weight: "bold", size: 12 },
                bodyFont: { size: 12 },
                bodySpacing: 6,
                usePointStyle: true,
                callbacks: {
                    title: (items) => items[0]?.label || "",
                    label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
                    labelPointStyle: (ctx) => ({
                        pointStyle: "circle",
                        rotation: 0,
                    }),
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: gridColor, drawBorder: false },
                ticks: { color: textColor, font: { size: 10, weight: "500" }, maxTicksLimit: 5, stepSize: 1, precision: 0 },
                border: { display: false },
            },
            x: {
                grid: { display: false },
                ticks: {
                    color: textColor,
                    font: { size: 9, weight: "500" },
                    maxRotation: 45,
                    minRotation: 0,
                    maxTicksLimit: 10,
                },
                border: { display: false },
            },
        },
    }), [isDarkMode, gridColor, textColor])

    return (
        <div className="rounded-2xl border border-slate-200/60 bg-white shadow-sm dark:border-slate-700/60 dark:bg-slate-900/80 overflow-hidden">
            {/* Accent gradient bar */}
            <div className="h-0.5 bg-gradient-to-r from-sky-400 via-indigo-400 to-emerald-400" />

            <div className="p-5">
                {/* Header compacto con mini-stats inline */}
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-950/40 dark:to-indigo-950/40">
                            <Activity className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                        </div>
                        <div>
                            <h3 className="text-[13px] font-bold text-slate-900 dark:text-white leading-tight">Tendencia diaria</h3>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400">Últimos 30 días</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 dark:bg-sky-950/30 px-2 py-0.5 text-[10px] font-bold text-sky-700 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-900/40">
                            <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                            {totalLeads} leads
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-900/40">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            {totalInscritos} inscritos
                        </span>
                        {peakDay?.leads > 0 && (
                            <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-rose-50 dark:bg-rose-950/30 px-2 py-0.5 text-[10px] font-bold text-rose-700 dark:text-rose-300 ring-1 ring-rose-200 dark:ring-rose-900/40">
                                <Flame className="h-2.5 w-2.5" />
                                Pico: {peakDay.label}
                            </span>
                        )}
                    </div>
                </div>

                {/* Toggles de canales — chips con icono de marca */}
                <div className="flex items-center flex-wrap gap-1.5 mb-3">
                    <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 mr-1">
                        Canales:
                    </span>
                    {CHANNEL_FILTERS.map((c) => {
                        const active = activeChannels.has(c.key)
                        const total = channelTotals[c.key] || 0
                        return (
                            <button
                                key={c.key}
                                type="button"
                                onClick={() => toggleChannel(c.key)}
                                className={`group inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border text-[10px] font-bold transition-all ${
                                    active
                                        ? "border-transparent text-white shadow-sm"
                                        : "border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900"
                                }`}
                                style={active ? { background: c.color } : {}}
                            >
                                <LeadSourceBadge source={c.key} size={16} ringed={active} />
                                <span>{c.label}</span>
                                <span className={`tabular-nums ${active ? "text-white/90" : "text-slate-400"}`}>
                                    · {total}
                                </span>
                            </button>
                        )
                    })}
                    {activeChannels.size > 0 && (
                        <button
                            type="button"
                            onClick={() => setActiveChannels(new Set())}
                            className="text-[9px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider ml-1"
                        >
                            Limpiar
                        </button>
                    )}
                </div>

                {/* Chart compacto */}
                <div className="h-[160px] sm:h-[180px]">
                    <Line data={chartData} options={chartOptions} />
                </div>

                {/* Leyenda enriquecida — visible solo cuando hay canales activos */}
                {activeChannels.size > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                        <div className="flex items-center justify-center flex-wrap gap-3">
                            {CHANNEL_FILTERS.filter((c) => activeChannels.has(c.key)).map((c) => (
                                <div
                                    key={c.key}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/60"
                                >
                                    <LeadSourceBadge source={c.key} size={28} ringed={false} />
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-[11px] font-bold text-slate-800 dark:text-slate-100 leading-tight">
                                            {c.label}
                                        </span>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            {/* Indicador de línea punteada igual a la del chart */}
                                            <span
                                                className="inline-block h-0 w-7 border-t-2 border-dashed"
                                                style={{ borderColor: c.color }}
                                            />
                                            <span className="text-[10px] font-extrabold tabular-nums" style={{ color: c.color }}>
                                                {channelTotals[c.key] || 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Legend + Trend indicator footer */}
                {/* Trend indicator compacto (solo si hay cambios) */}
                {(weekTrend.leads !== 0 || weekTrend.inscritos !== 0) && (
                    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2">
                        <span className="text-[9px] text-slate-400 uppercase tracking-wider">vs semana anterior:</span>
                        {weekTrend.leads !== 0 && (
                            <span className={`text-[10px] font-bold ${weekTrend.leads > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"}`}>
                                {weekTrend.leads > 0 ? "+" : ""}{weekTrend.leads}% leads
                            </span>
                        )}
                        {weekTrend.leads !== 0 && weekTrend.inscritos !== 0 && <span className="text-slate-300 dark:text-slate-600">·</span>}
                        {weekTrend.inscritos !== 0 && (
                            <span className={`text-[10px] font-bold ${weekTrend.inscritos > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"}`}>
                                {weekTrend.inscritos > 0 ? "+" : ""}{weekTrend.inscritos}% inscritos
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
